import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { PRICE_LABEL, startCheckout } from "@/lib/checkout";
import { T } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";

const LINE1 = "LULA, PT E OS";
const LINE2 = "ESCÂNDALOS";
const LINE3 = "QUE MARCARAM";
const LINE4 = "UMA ERA.";

const archive = [
  "https://images.unsplash.com/photo-1523995462485-3d171b5c8fa9?w=900&q=80",
  "https://images.unsplash.com/photo-1544216717-3bbf52512659?w=900&q=80",
  "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=900&q=80",
  "https://images.unsplash.com/photo-1589216532372-1c2a367900d9?w=900&q=80",
];

export default function Hero() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const h = () => setY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
  };
  const line = {
    hidden: { y: "110%" },
    show: { y: 0, transition: { duration: 1.0, ease: [0.19, 1, 0.22, 1] } },
  };

  return (
    <section
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-[#050505] scanlines"
    >
      {/* Background collage */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-[0.35]"
          style={{ transform: `translateY(${y * 0.15}px)` }}
        >
          {archive.map((src, i) => (
            <div key={i} className="relative overflow-hidden">
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover"
                style={{ filter: "grayscale(100%) contrast(1.15) brightness(0.65)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-red-950/30 via-black/40 to-black/90" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#050505_80%)]" />
        {/* Grid guide */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Side eyebrow */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-left eyebrow z-10">
        Dossiê · Volume 01 · Edição Independente · 2026
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-32 md:pt-40 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-[1px] w-10 bg-red-600" />
          <span className="eyebrow">Arquivo histórico · Documento 2026</span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display font-black tracking-[-0.02em] leading-[0.92] text-[13vw] md:text-[9.5vw] lg:text-[160px] text-neutral-50"
        >
          {[LINE1, LINE2, LINE3, LINE4].map((t, i) => (
            <span className="hero-mask-line" key={i}>
              <motion.span variants={line} className={i === 1 ? "text-red-600 italic" : ""}>
                {t}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-10 max-w-xl text-[15px] md:text-base text-neutral-400 leading-relaxed"
        >
          Uma análise documental dos principais acontecimentos políticos, investigações, escândalos,
          controvérsias e processos judiciais que marcaram a história recente do Brasil.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Button
            data-testid={T.heroBuy}
            onClick={startCheckout}
            className="btn-pulse group bg-red-600 hover:bg-red-700 text-white rounded-sm px-7 h-14 text-[13px] tracking-[0.25em] uppercase font-mono"
          >
            Quero o ebook — {PRICE_LABEL}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <a
            data-testid={T.heroExplore}
            href="#casos"
            className="inline-flex items-center justify-center h-14 px-6 border border-white/15 text-neutral-300 hover:border-red-600 hover:text-red-500 transition-colors text-[12px] tracking-[0.25em] uppercase font-mono"
          >
            Conheça o conteúdo
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500"
        >
          <div><span className="text-red-500">01 ·</span> Mensalão</div>
          <div><span className="text-red-500">02 ·</span> Lava Jato</div>
          <div><span className="text-red-500">03 ·</span> Triplex</div>
          <div><span className="text-red-500">04 ·</span> Governos PT</div>
        </motion.div>
      </div>

      {/* Bottom corner marks */}
      <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-600">
        <div>
          <div className="text-red-500 mb-1">LAT · -15.78°</div>
          <div>Brasília · DF</div>
        </div>
        <div className="text-right">
          <div className="text-red-500 mb-1">REF · 2003 → 2021</div>
          <div>Linha cronológica</div>
        </div>
      </div>
    </section>
  );
}
